import { useMemo, useState } from 'react';
import type { DateSlot, EventType, SlateDate } from '../../poller/types.js';
import { REGIONS, regionName } from '../../regions.js';
import { FormatIcon } from '../components/FormatIcon.tsx';
import { SiteWatch } from '../components/SiteWatch.tsx';
import type { SiteState } from '../client.ts';
import { WikiPanel } from '../components/WikiPanel.tsx';
import { webSearchUrl } from '../lib/websearch.js';
import { ItemDates, ItemHead, PartOf, Seasons, Works } from './DetailBody.tsx';
import { placeholderFor, regionalReleases } from '../lib/derive.js';
import { FORMAT_SLOTS, formatLabel } from '../lib/formats.js';
import { formatShortDate, headlineFor } from '../lib/format.js';
import type { WatchEvent, WatchItem } from '../lib/model.js';

interface Props {
  item: WatchItem;
  events: readonly WatchEvent[];
  now: Date;
  onBack: () => void;
  onToggleConsumed: (consumed: boolean) => void;
  onSetEventTypes: (types: EventType[]) => void;
  onSetPreferences: (preferences: { formats?: DateSlot[]; region?: string | null }) => void;
  onRemove: () => void;
  eventTypes: readonly EventType[];
  /** Go and fetch the cover, blurb, editions and dates again, now. */
  onRefresh: () => Promise<string | null>;
  /** The author's own site: set the address, look at it, mark it read. */
  onSetSite: (url: string | null) => Promise<SiteState | null>;
  onCheckSite: () => Promise<SiteState | null>;
  onSiteSeen: () => void;
  /** The series this belongs to, if it is also on the reader's slate. */
  seriesOnSlate?: WatchItem | null;
  onOpenItem?: (item: WatchItem) => void;
}

/**
 * Everything the app knows about one thing, including — importantly — what it
 * doesn't. An empty date is information rather than a gap to hide.
 */
export function Detail(props: Props) {
  const { item, events, now, onBack } = props;

  const placeholder = useMemo(() => placeholderFor(item, now), [item, now]);
  const isPlaceholder = placeholder?.kind === 'placeholder';
  const releases = regionalReleases(item);

  const history = events
    .filter((e) => e.watchableId === item.watchableId)
    .slice()
    .sort((a, b) => b.detectedAt.localeCompare(a.detectedAt))
    .slice(0, 8);

  return (
    <>
      <div className="appbar">
        <button className="wordmark" onClick={onBack}>
          ← slate
        </button>
      </div>

      <ItemHead item={item} now={now} />

      <Actions
        item={item}
        onRefresh={props.onRefresh}
        onSetSite={props.onSetSite}
        onCheckSite={props.onCheckSite}
        onSiteSeen={props.onSiteSeen}
      />

      <WikiPanel item={item} />

      <ItemDates item={item} now={now} />

      {/* Outside any placeholder guard on purpose. "Nevermoor, Book 5" is the
          view where the list of the four that *do* exist is most use — it is
          the evidence behind the guess. Each of these renders nothing when it
          has nothing, so there is no empty section to hide. */}
      <PartOf
        item={item}
        followed={props.seriesOnSlate ?? null}
        onOpen={props.onOpenItem ?? (() => {})}
      />
      <Works item={item} />
      <Seasons item={item} />

      {!isPlaceholder && <Preferences item={item} onSet={props.onSetPreferences} />}

      <TellMeWhen {...props} />

      {history.length > 0 && (
        <>
          <div className="section" style={{ marginTop: 22 }}>
            <div className="section-head">
              <span className="label">What's happened</span>
            </div>
          </div>
          <ul className="history">
            {history.map((event) => {
              const headline = headlineFor(event.type, event.payload);
              return (
                <li key={event.id}>
                  <span className="when">{formatShortDate(event.detectedAt)}</span>
                  <span>
                    {headline.label} — {headline.detail}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {!isPlaceholder && hasAnyDate(releases) && (
        <div className="section" style={{ marginTop: 22 }}>
          <button
            type="button"
            className="primary-btn"
            onClick={() => props.onToggleConsumed(!item.consumedAt)}
          >
            {item.consumedAt ? 'Put back on the list' : markWord(item)}
          </button>
        </div>
      )}

      <button className="danger" onClick={props.onRemove}>
        Stop watching this
      </button>
    </>
  );
}

/**
 * Two ways to learn more: ask us to look again, or go and read the web.
 *
 * The second is a plain link out on purpose. Slate only ever states what two
 * catalogues will vouch for, which is right for anything that can become a
 * notification — but it leaves nowhere to go when you just want to read about
 * a thing, and a link cannot be wrong.
 */
function Actions({
  item,
  onRefresh,
  onSetSite,
  onCheckSite,
  onSiteSeen,
}: {
  item: WatchItem;
  onRefresh: () => Promise<string | null>;
  onSetSite: (url: string | null) => Promise<SiteState | null>;
  onCheckSite: () => Promise<SiteState | null>;
  onSiteSeen: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);

  async function refresh() {
    setBusy(true);
    setSaid(null);
    try {
      setSaid(await onRefresh());
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="actions">
        <button type="button" className="action" disabled={busy} onClick={() => void refresh()}>
          {busy ? 'Looking…' : 'Refresh'}
        </button>
        {/* No change marker here, ever. This runs a search nobody diffed, so a
            mark on it would assert a check that never happened. */}
        <a className="action" href={webSearchUrl(item)} target="_blank" rel="noopener noreferrer">
          Search the web ↗
        </a>
      </div>
      {said && <p className="hint">{said}</p>}
      <SiteWatch item={item} onSet={onSetSite} onCheck={onCheckSite} onSeen={onSiteSeen} />
    </>
  );
}

/**
 * Which formats count, and whose dates to use.
 *
 * These change what the home screen does, not just what it says, so they sit
 * on the item's own page where the consequence can be spelled out — rather
 * than in a settings screen where the connection would be lost.
 */
function Preferences({
  item,
  onSet,
}: {
  item: WatchItem;
  onSet: (preferences: { formats?: DateSlot[]; region?: string | null }) => void;
}) {
  // A series or an author has no formats of its own: the books under it do.
  const choosable = item.kind === 'title' ? FORMAT_SLOTS[item.medium] : [];
  const all = item.formats.length === 0;

  function toggle(slot: DateSlot) {
    const current = all ? [...FORMAT_SLOTS[item.medium]] : [...item.formats];
    const next = current.includes(slot)
      ? current.filter((s) => s !== slot)
      : [...current, slot];
    // Turning the last one off means "any", not "none" — the alternative is a
    // subscription that can never tell you anything, which nobody wants.
    onSet({ formats: next.length === choosable.length ? [] : next });
  }

  return (
    <>
      <div className="section" style={{ marginTop: 22 }}>
        <div className="section-head">
          <span className="label">What counts as out</span>
        </div>
      </div>

      {choosable.length > 0 ? (
        <div className="options plain">
          <div className="picks">
            {choosable.map((slot) => {
              const on = all || item.formats.includes(slot);
              return (
                <button
                  key={slot}
                  type="button"
                  className={on ? 'pick on' : 'pick'}
                  aria-pressed={on}
                  onClick={() => toggle(slot)}
                >
                  <FormatIcon slot={slot} medium={item.medium} size={15} />
                  {formatLabel(slot, item.medium)}
                </button>
              );
            })}
          </div>
          <p className="options-note">
            {all
              ? 'Any format — whichever arrives first makes it ready.'
              : 'Only these make it ready. The others still show above.'}
          </p>
        </div>
      ) : (
        <p className="hint">
          Formats belong to the books themselves — add one to choose between hardback,
          ebook and audiobook.
        </p>
      )}

      <div className="section" style={{ marginTop: 18 }}>
        <div className="section-head">
          <span className="label">Region</span>
        </div>
      </div>
      <div className="options plain">
        <select
          className="field"
          value={item.region ?? ''}
          aria-label="Region"
          onChange={(e) => onSet({ region: e.target.value || null })}
        >
          <option value="">Wherever it lands first</option>
          {REGIONS.map((r) => (
            <option key={r.code} value={r.code}>
              {r.name}
            </option>
          ))}
        </select>
        <p className="options-note">
          {item.medium === 'book'
            ? 'Kept with this. Books have no per-region dates; an edition’s own country is shown above where the catalogue records it, and a missing edition is not proof it is unavailable.'
            : item.region
              ? `Showing ${regionName(item.region)} dates where TMDB has them.`
              : 'Release dates differ by country; this picks whose to show.'}
        </p>
      </div>
    </>
  );
}

const TOGGLES: Array<{ type: EventType; label: string }> = [
  { type: 'date_changed', label: 'The date changes' },
  { type: 'released', label: "It's out" },
  { type: 'format_added', label: 'Another format lands' },
  { type: 'work_added', label: 'Something new is listed' },
];

function TellMeWhen({
  eventTypes,
  onSetEventTypes,
}: Pick<Props, 'eventTypes' | 'onSetEventTypes'>) {
  // Empty means all: nobody picks event types at add time, so an untouched
  // subscription must mean "tell me everything", not "tell me nothing".
  const all = eventTypes.length === 0;

  function toggle(type: EventType) {
    const current = all ? TOGGLES.map((t) => t.type) : [...eventTypes];
    const next = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    onSetEventTypes(next);
  }

  return (
    <>
      <div className="section" style={{ marginTop: 22 }}>
        <div className="section-head">
          <span className="label">Tell me when</span>
        </div>
      </div>
      <ul className="rows">
        {TOGGLES.map(({ type, label }) => {
          const on = all || eventTypes.includes(type);
          return (
            <li key={type}>
              <span className="k">{label}</span>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                aria-label={label}
                className={on ? 'toggle on' : 'toggle'}
                onClick={() => toggle(type)}
              />
            </li>
          );
        })}
      </ul>
    </>
  );
}

function hasAnyDate(releases: Partial<Record<DateSlot, SlateDate | null>>): boolean {
  return Object.values(releases).some((d) => d != null);
}

function markWord(item: WatchItem): string {
  return item.medium === 'book' ? 'Mark as read' : 'Mark as watched';
}
